import type { Catalogo, Cotizacion, Pieza } from "./types";

export interface CambioPrecio {
  pieza: string;
  antes: number;
  ahora: number;
}

// Una cotización guardada congela el precio unitario de cada pieza, pero n8n
// cobra con el precio VIGENTE de Airtable (ver resolverPieza en
// cotizador-calcular-precio.json). Sin esto, al reabrir una cotización después
// de un cambio de catálogo la vista previa muestra un total distinto del que
// se valida y guarda. Mismo orden de resolución que n8n; el ajuste manual del
// vendedor, las piezas personalizadas y las que se cotizan por su lista de
// materiales no dependen del catálogo y no se tocan.
export function preciosVigentes(
  c: Cotizacion,
  cat: Catalogo,
): { cotizacion: Cotizacion; cambios: CambioPrecio[] } {
  const cambios: CambioPrecio[] = [];
  const vigente = (p: Pieza): Pieza => {
    if (
      p.precioAjustado != null ||
      p.personalizado ||
      p.esPersonalizada ||
      p.materiales?.length
    )
      return p;
    const nombre = p.catalogoEspecial?.codigoArticulo
      ? `${p.variante} (${p.catalogoEspecial.codigoArticulo})`
      : p.variante;
    if (p.catalogoEspecial) {
      const item = cat.catalogoClienteEspecial.find(
        (x) => x.id === p.catalogoEspecial!.id,
      );
      if (
        item?.precioUnitario == null ||
        item.precioUnitario === p.precioUnitario
      )
        return p;
      cambios.push({
        pieza: nombre,
        antes: p.precioUnitario ?? 0,
        ahora: item.precioUnitario,
      });
      return { ...p, precioUnitario: item.precioUnitario };
    }
    const contrato = cat.preciosContrato.find(
      (x) =>
        x.clienteContrato === c.cliente.cliente_contrato &&
        x.familia === p.familia &&
        x.variante === p.variante,
    );
    // También la pieza que no tenía contrato y ahora sí: n8n recibe el
    // precio de catálogo como precio final (ver construirPayload) y ya no
    // lo cambia por el de contrato.
    if (p.precioContratado != null || contrato) {
      if (!contrato || contrato.precioContratado === p.precioContratado)
        return p;
      cambios.push({
        pieza: nombre,
        antes: p.precioContratado ?? p.precioUnitario ?? 0,
        ahora: contrato.precioContratado,
      });
      return { ...p, precioContratado: contrato.precioContratado };
    }
    const item = cat.catalogo.find(
      (x) => x.familia === p.familia && x.variante === p.variante,
    );
    if (item?.precio == null || item.precio === p.precioUnitario) return p;
    cambios.push({
      pieza: nombre,
      antes: p.precioUnitario ?? 0,
      ahora: item.precio,
    });
    return { ...p, precioUnitario: item.precio };
  };
  const sitios = c.sitios.map((s) => ({
    ...s,
    productos: s.productos.map((pr) => ({
      ...pr,
      piezas: pr.piezas.map(vigente),
    })),
  }));
  return { cotizacion: cambios.length ? { ...c, sitios } : c, cambios };
}
